/**
 * Tab definitions for script-based transforms
 * @type {Array<{id: string, labelKey: string, contentKey: string, description: string, primary?: boolean, group?: {id: string, title: string}}>}
 */
const SCRIPT_TRANSFORM_TABS = [
    {
        id: 'VALUE_TRANSFORM',
        labelKey: 'valueTransform',
        contentKey: 'value_transform',
        description: 'Transform the record value',
        primary: true,
        group: { id: 'transforms', title: 'Value' }
    },
    {
        id: 'KEY_TRANSFORM',
        labelKey: 'keyTransform',
        contentKey: 'key_transform',
        description: 'Transform the record key',
        group: { id: 'transforms', title: 'Value' }
    },
    {
        id: 'TOPIC_TRANSFORM',
        labelKey: 'topicTransform',
        contentKey: 'topic_transform',
        description: 'Transform the record topic',
        group: { id: 'transforms', title: 'Value' }
    },
    {
        id: 'COMMON_TRANSFORM',
        labelKey: 'commonTransform',
        contentKey: 'common_transform',
        description: 'Compute common data needed by all transforms',
        group: { id: 'transforms', title: 'Value' }
    },
    {
        id: 'VALUE_SCHEMA_TRANSFORM',
        labelKey: 'valueSchemaTransform',
        contentKey: 'value_schema_transform',
        description: 'Transform the record value schema',
        group: { id: 'schemas', title: 'Schema' }
    },
    {
        id: 'KEY_SCHEMA_TRANSFORM',
        labelKey: 'keySchemaTransform',
        contentKey: 'key_schema_transform',
        description: 'Transform the record key schema',
        group: { id: 'schemas', title: 'Schema' }
    }
];

/**
 * Creates editor tabs configuration for script-based transforms (JavaScript/Python)
 * @param {Object} implementation - The implementation object containing transform code
 * @param {string} language - The language type ('javascript' or 'python')
 * @returns {Array} Array of tab configuration objects
 */
export function createScriptTransformTabs(implementation, language) {
    const fileExtension = language === 'javascript' ? '.js' : '.py';

    return SCRIPT_TRANSFORM_TABS.map((tabConfig) => ({
        id: tabConfig.id,
        label: `${tabConfig.labelKey}${fileExtension}`,
        type: tabConfig.id,
        content: implementation ? (implementation[tabConfig.contentKey] || '') : '', // Use saved implementation data if available, otherwise empty
        icon: 'FileCode2',
        description: tabConfig.description,
        ...(tabConfig.primary && { primary: true }),
        ...(tabConfig.group && { group: tabConfig.group })
    }));
}

/**
 * Creates editor tabs configuration for SQL enrich transforms
 * @param {Object} implementation - The implementation object containing SQL code
 * @returns {Array} Array of tab configuration objects
 */
export function createEnrichTransformTabs(implementation) {
    return [
        {
            id: 'ENRICH_SQL',
            label: 'Enrich SQL',
            type: 'ENRICH_SQL',
            content: implementation ? (implementation.enrichSQL || '') : '', // Use saved implementation data if available, otherwise empty
            icon: 'FileCode2',
            description: 'SQL query to enrich main table records with lookup table data',
            primary: true,
            group: { id: 'queries', title: 'Value' }
        },
        {
            id: 'CREATE_TABLE_MAIN',
            label: 'Main Table Schema',
            type: 'CREATE_TABLE_MAIN',
            content: implementation ? (implementation.mainTable?.createTableSQL || '') : '', // Use saved implementation data if available, otherwise empty
            icon: 'FileCode2',
            description: 'Define the schema for the main table (stream to be enriched)',
            group: { id: 'schemas', title: 'Schema' }
        },
        {
            id: 'CREATE_TABLE_LOOKUP',
            label: 'Lookup Table Schema',
            type: 'CREATE_TABLE_LOOKUP',
            content: implementation ? (implementation.lookupTable?.createTableSQL || '') : '', // Use saved implementation data if available, otherwise empty
            icon: 'FileCode2',
            description: 'Define the schema for the lookup table (containing enrichment data)',
            group: { id: 'schemas', title: 'Schema' }
        }
    ];
}

/**
 * Creates editor tabs configuration for SQL join transforms
 * @param {Object} implementation - The implementation object containing SQL code
 * @param {number} tableCount - Number of input tables for the join (minimum 2)
 * @returns {Array} Array of tab configuration objects
 */
export function createJoinTransformTabs(implementation, tableCount = 2) {
    const tabs = [];

    // Add JOIN_SQL tab first (primary tab)
    tabs.push({
        id: 'JOIN_SQL',
        label: 'Join SQL',
        type: 'JOIN_SQL',
        content: implementation ? (implementation.joinSQL || '') : '', // Use saved implementation data if available, otherwise empty
        icon: 'FileCode2',
        description: 'SQL query to join multiple input tables together',
        primary: true,
        group: { id: 'queries', title: 'SQL Queries' }
    });

    // Add CREATE_TABLE tabs based on tableCount (minimum 2 for SQL joins)
    const actualTableCount = Math.max(2, tableCount);
    for (let i = 0; i < actualTableCount; i++) {
        tabs.push({
            id: `CREATE_TABLE_${i + 1}`,
            label: `Table ${i + 1} Schema`,
            type: `CREATE_TABLE_${i + 1}`,
            content: implementation ? (implementation.inputTables?.[i]?.createTableSQL || '') : '', // Use saved implementation data if available, otherwise empty
            icon: 'FileCode2',
            description: `Define the schema for input table ${i + 1} used in the join`,
            group: { id: 'schemas', title: 'Schema' }
        });
    }

    return tabs;
}

/**
 * Creates editor tabs configuration for SQL transforms (enrich or join)
 * @param {Object} implementation - The implementation object containing SQL code
 * @param {string} transformType - The transform type ('enrich' or 'sql_join')
 * @param {number} [tableCount] - Number of input tables for join transforms
 * @returns {Array} Array of tab configuration objects
 */
export function createSqlTransformTabs(implementation, transformType, tableCount) {
    if (!implementation) return [];

    if (transformType === 'enrich') {
        return createEnrichTransformTabs(implementation);
    } else if (transformType === 'sql_join') {
        return createJoinTransformTabs(implementation, tableCount);
    }

    return [];
}
